import {
  FlowModuleItemType,
  ModuleInputKeyEnum,
  VariableItemType,
} from "@packages/flow-core/module";
import { NodeProps } from "reactflow";
import NodeCard from "../NodeCard";
import Divider from "../Divider";
import styles from "./index.less";
import RenderInput from "../render/RenderInput";
import { RenderInputProps } from "../render/RenderInput/types";
import { Badge, Button, Col, Row, Select, Table, Tabs } from "antd";
import { useFlow } from "../FlowEdit/useFlow";
import { PlusOutlined } from "@ant-design/icons";
import JsonEditor from "../Editor/JsonEditor";
import { memo, useMemo, useState, useTransition } from "react";
import { FlowNodeTypeEnum } from "@packages/flow-core/module/node/constants";
import { nanoid } from "nanoid";
import { EditorVariablePickerType } from "../Editor/types";
import HttpInput from "../HttpInput";
import RenderOutput from "../render/RenderOutput";
import RenderToolInput from "../render/RenderToolInput";
import TextInput from "../render/RenderInput/templates/TextInput";
import { ReactComponent as DeleteIcon } from "../../../assets/delete.svg";
import classNames from "classnames";

type ParamDataType = {
  key?: string;
  value?: string;
  id: string;
};

const HttpHeaders = [
  { key: "A-IM", label: "A-IM" },
  { key: "Accept", label: "Accept" },
  { key: "Accept-Charset", label: "Accept-Charset" },
  { key: "Accept-Encoding", label: "Accept-Encoding" },
  { key: "Accept-Language", label: "Accept-Language" },
  { key: "Accept-Datetime", label: "Accept-Datetime" },
  {
    key: "Access-Control-Request-Method",
    label: "Access-Control-Request-Method",
  },
  {
    key: "Access-Control-Request-Headers",
    label: "Access-Control-Request-Headers",
  },
  { key: "Authorization", label: "Authorization" },
  { key: "Cache-Control", label: "Cache-Control" },
  { key: "Connection", label: "Connection" },
  { key: "Content-Length", label: "Content-Length" },
  { key: "Content-Type", label: "Content-Type" },
  { key: "Cookie", label: "Cookie" },
  { key: "Date", label: "Date" },
  { key: "Expect", label: "Expect" },
  { key: "Forwarded", label: "Forwarded" },
  { key: "From", label: "From" },
  { key: "Host", label: "Host" },
  { key: "If-Match", label: "If-Match" },
  { key: "If-Modified-Since", label: "If-Modified-Since" },
  { key: "If-None-Match", label: "If-None-Match" },
  { key: "If-Range", label: "If-Range" },
  { key: "If-Unmodified-Since", label: "If-Unmodified-Since" },
  { key: "Max-Forwards", label: "Max-Forwards" },
  { key: "Origin", label: "Origin" },
  { key: "Pragma", label: "Pragma" },
  { key: "Proxy-Authorization", label: "Proxy-Authorization" },
  { key: "Range", label: "Range" },
  { key: "Referer", label: "Referer" },
  { key: "TE", label: "TE" },
  { key: "User-Agent", label: "User-Agent" },
  { key: "Upgrade", label: "Upgrade" },
  { key: "Via", label: "Via" },
  { key: "Warning", label: "Warning" },
  { key: "Dnt", label: "Dnt" },
  { key: "X-Requested-With", label: "X-Requested-With" },
  { key: "X-CSRF-Token", label: "X-CSRF-Token" },
];

const HttpMethodAndUrl = memo(({ inputs, moduleId }: RenderInputProps) => {
  const { updateNodeValue } = useFlow();
  const methodNode = inputs!.find(
    (item) => item.key === ModuleInputKeyEnum.httpMethod
  )!;
  const urlNode = inputs!.find(
    (item) => item.key === ModuleInputKeyEnum.httpReqUrl
  )!;

  return (
    <Row style={{ width: "100%" }} wrap={false}>
      <Col span={6}>
        <Select
          options={methodNode!.list!}
          style={{ width: "100%" }}
          defaultValue={methodNode?.value}
          onChange={(v) => {
            updateNodeValue(moduleId, methodNode.key, v);
          }}
        />
      </Col>
      <Col flex={1} style={{ marginLeft: 10 }}>
        <TextInput moduleId={moduleId} inputs={inputs} item={urlNode} />
      </Col>
    </Row>
  );
});

const RenderForm = memo(
  ({
    moduleId,
    item,
    variables,
    isHeader,
  }: RenderInputProps & {
    variables?: EditorVariablePickerType[];
    isHeader?: boolean;
  }) => {
    const { updateNodeValue } = useFlow();
    const [updateTrigger, setUpdateTrigger] = useState(false);

    function handleAddParam() {
      const newValue = ((item?.value as ParamDataType[]) || []).concat({
        key: undefined,
        value: undefined,
        id: nanoid(),
      });
      updateNodeValue(moduleId, item!.key, newValue);
    }

    return (
      <div>
        <Table<ParamDataType>
          className={classNames("pro-table", styles.table)}
          dataSource={item?.value as ParamDataType[]}
          rowKey="id"
          columns={[
            {
              title: "参数名",
              dataIndex: "key",
              width: 100,
              render(_v, data, index) {
                return (
                  <HttpInput
                    hasVariablePlugin={false}
                    hasDropDownPlugin={isHeader}
                    variables={HttpHeaders}
                    placeholder="请输入参数名"
                    value={data.key}
                    setDropdownValue={(v) => {
                      const newValue = [...(item!.value as ParamDataType[])];
                      const valueItem = {
                        ...data,
                        key: v,
                      };
                      newValue.splice(index, 1, valueItem);
                      updateNodeValue(moduleId, item!.key, newValue);
                      setUpdateTrigger((prev) => !prev);
                    }}
                    updateTrigger={updateTrigger}
                    onChange={(v) => {
                      const newValue = [...(item!.value as ParamDataType[])];
                      const valueItem = {
                        ...data,
                        key: v,
                      };
                      newValue.splice(index, 1, valueItem);
                      updateNodeValue(moduleId, item!.key, newValue);
                    }}
                  />
                );
              },
            },
            {
              title: "参数值",
              dataIndex: "value",
              width: 100,
              render(_v, data, index) {
                return (
                  <HttpInput
                    placeholder="请输入参数值"
                    variables={variables}
                    value={data.value}
                    onChange={(v) => {
                      const newValue = [...(item!.value as ParamDataType[])];
                      const valueItem = {
                        ...data,
                        value: v,
                      };
                      newValue.splice(index, 1, valueItem);
                      updateNodeValue(moduleId, item!.key, newValue);
                    }}
                  />
                );
              },
            },
            {
              title: "操作",
              width: 30,
              align: "center",
              render(_v, data) {
                return (
                  <DeleteIcon
                    className={styles.danger}
                    onClick={() => {
                      const newValue = (item!.value as ParamDataType[]).filter(
                        (item) => item.id !== data.id
                      );
                      updateNodeValue(moduleId, item!.key, newValue);
                    }}
                  />
                );
              },
            },
          ]}
          size="small"
          bordered
          pagination={false}
          locale={{ emptyText: "暂无数据" }}
        />
        <Button
          block
          icon={<PlusOutlined color="#646A73" />}
          onClick={handleAddParam}
          className={styles["add-row-btn"]}
        >
          添加参数
        </Button>
      </div>
    );
  }
);

const HttpParam = memo(({ inputs, moduleId }: RenderInputProps) => {
  const { updateNodeValue, getAllNodes, systemVariables = [] } = useFlow();
  const paramsNode = inputs!.find(
    (item) => item.key === ModuleInputKeyEnum.httpParams
  );
  const headNode = inputs!.find(
    (item) => item.key === ModuleInputKeyEnum.httpHeaders
  );
  const bodyNode = inputs!.find(
    (item) => item.key === ModuleInputKeyEnum.httpBody
  );

  const [, startSts] = useTransition();

  const variables = useMemo(() => {
    const templteVariables =
      getAllNodes()
        .find((node) => node.data.flowType === FlowNodeTypeEnum.templateParam)
        ?.data?.inputs.map((item) => ({
          key: item.key,
          label: item.label,
          isTemplateVariable: true,
        })) || [];
    const globaVariablelModule: Pick<VariableItemType, "key" | "label">[] =
      getAllNodes()
        .find((node) => node.data.flowType === FlowNodeTypeEnum.globalVariable)
        ?.data?.inputs.filter((input) => input.edit)
        .map((item) => ({ key: item.key, label: item.label })) || [];
    const moduleVariables =
      inputs
        ?.filter((input) => input.edit || input.toolDescription)
        .map((item) => ({
          key: item.key,
          label: item.description || item.label,
          self: true,
        })) || [];
    return [
      ...templteVariables,
      ...globaVariablelModule,
      ...moduleVariables,
      ...systemVariables,
    ];
  }, [getAllNodes, systemVariables, inputs]);

  return (
    <Tabs
      size="small"
      className={styles.tabs}
      items={[
        {
          key: "params",
          label: (
            <div>
              Params
              <Badge
                className={styles.badge}
                count={((paramsNode?.value as []) || []).length}
                color="#F3F3F3"
              />
            </div>
          ),
          children: (
            <RenderForm
              inputs={inputs}
              item={paramsNode!}
              moduleId={moduleId}
              variables={variables}
            />
          ),
        },
        {
          label: "Body",
          key: "body",
          children: (
            <JsonEditor
              placeholder="json语法"
              defaultHeight={200}
              variables={variables}
              resize
              value={bodyNode?.value as string}
              onChange={(e) => {
                startSts(() => {
                  updateNodeValue(moduleId, bodyNode!.key, e);
                });
              }}
            />
          ),
        },
        {
          label: (
            <div>
              Header
              <Badge
                className={styles.badge}
                count={((headNode?.value as []) || []).length}
                color="#F3F3F3"
              />
            </div>
          ),
          key: "header",
          children: (
            <RenderForm
              inputs={inputs}
              item={headNode!}
              moduleId={moduleId}
              variables={variables}
              isHeader
            />
          ),
        },
      ]}
    />
  );
});

export default memo(function NodeHttp(props: NodeProps<FlowModuleItemType>) {
  const { data, selected } = props;
  const { moduleId, inputs, outputs } = data;
  const { hasToolNode, splitToolInputs } = useFlow();
  const { toolInputs, commonInputs } = splitToolInputs(inputs, moduleId);

  return (
    <NodeCard {...data} selected={selected}>
      {hasToolNode && (
        <>
          <Divider text="工具输入" />
          <div className={styles.content}>
            <RenderToolInput moduleId={moduleId} inputs={toolInputs} canEdit />
          </div>
        </>
      )}
      <Divider text="输入" />
      <div className={styles.content}>
        <RenderInput
          moduleId={moduleId}
          flowInputList={commonInputs}
          CustomComponent={{
            [ModuleInputKeyEnum.httpMethod]: HttpMethodAndUrl,
            [ModuleInputKeyEnum.httpHeaders]: HttpParam,
          }}
        />
      </div>
      <Divider text="输出" />
      <div className={styles.content}>
        <RenderOutput moduleId={moduleId} flowOutputList={outputs} />
      </div>
    </NodeCard>
  );
});
